import { SUP_CAMPO, SUP_PRE, SUP_VALIDACAO } from "@caint/data";
import { MatBadge, MatSelect } from "@caint/design-system";
import type { NivelMaturidade } from "@caint/engine";
import type {
  EscolhaResolucao,
  ParecerConfiabilidade,
  ResultadoSupervisao,
  SupervisaoData,
} from "@caint/shared";
import { Fragment, type ReactNode, useMemo, useState } from "react";
import {
  type IndicadoresParecer,
  LIMIAR_CHECKLIST_COMPLETO,
  PESOS_CONFORMIDADE_CAMPO,
  conformidadeFase,
  corroboracao,
  criterioOu,
  diagnosticoAmostragem,
  divergenciasAmostra,
  indicadoresParecer,
  itemCompleto,
  itemDoCatalogo,
  matParaResultado,
  minimoParaLiberar,
  pesoCriticidade,
} from "../lib/supervisao.ts";
import type { ItemMerged } from "../state/useAppState.ts";

/** Régua de liberação da amostra, em % inteiro (ex.: 90). */
const PCT_LIBERA = Math.round(LIMIAR_CHECKLIST_COMPLETO * 100);

type Editar = (mut: (d: SupervisaoData) => SupervisaoData) => void;

const PARECERES: { v: ParecerConfiabilidade; label: string }[] = [
  { v: "Confiavel", label: "Confiável" },
  { v: "Ressalvas", label: "Com ressalvas" },
  { v: "NaoConfiavel", label: "Não confiável" },
];

/* ---------- primitivos do visual do protótipo ---------- */

function mat(v: string | null | undefined): NivelMaturidade | "" {
  return (v ?? "") as NivelMaturidade | "";
}

const CRIT_COR = {
  Alta: ["var(--danger-soft)", "var(--danger)"],
  Média: ["var(--warning-soft)", "color-mix(in srgb, var(--warning) 72%, black)"],
  Baixa: ["var(--surface-2)", "var(--text-muted)"],
} as const;
function CritBadge({ crit }: { crit: string }) {
  const [bg, fg] = (CRIT_COR as Record<string, readonly [string, string]>)[crit] ?? CRIT_COR.Baixa;
  return (
    <span className="sup-flag" style={{ background: bg, color: fg }}>
      {crit}
    </span>
  );
}

const COR_RES: Record<ResultadoSupervisao, string> = {
  C: "var(--success)",
  NC: "var(--danger)",
  NA: "var(--text)",
};

/** Legenda da escala adaptada para contexto de supervisão/AUDIN. */
const RUBRICA_SUP: Record<string, string> = {
  "0": "Critério não realizado — ausente ou ineficaz.",
  "1": "Realizado com graves lacunas ou desvios críticos.",
  "2": "Realizado parcialmente; lacunas significativas.",
  "3": "Realizado com pequenas ressalvas; essencialmente adequado.",
  "4": "Plenamente realizado — atende todos os critérios.",
  na: "Não se aplica nesta visita; excluído do cálculo.",
};
const MATCLR_SUP: Record<string, string> = {
  "0": "var(--mat-0)",
  "1": "var(--mat-1)",
  "2": "var(--mat-2)",
  "3": "var(--mat-3)",
  "4": "var(--mat-4)",
  na: "var(--mat-na)",
};
const TITULO_SUP: Record<string, string> = {
  "0": "Não realizado",
  "1": "Graves lacunas",
  "2": "Parcial",
  "3": "Adequado",
  "4": "Pleno",
  na: "Não se aplica",
};

function DSLegendaSup({ matAtual }: { matAtual: NivelMaturidade | null }) {
  const niveis: NivelMaturidade[] = ["0", "1", "2", "3", "4", "na"];
  return (
    <div style={{ marginTop: "var(--space-3)" }}>
      <div
        style={{
          fontSize: "var(--text-2xs)",
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          color: "var(--text-faint)",
          fontWeight: 600,
          marginBottom: 6,
        }}
      >
        Escala de aderência do processo supervisionado
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6 }}>
        {niveis.map((k) => {
          const on = matAtual === k;
          return (
            <div
              key={k}
              style={{
                display: "flex",
                gap: 8,
                alignItems: "flex-start",
                padding: "8px 9px",
                borderRadius: "var(--radius-sm)",
                background: on ? "var(--surface-2)" : "transparent",
                border: on ? "1px solid var(--border-strong)" : "1px solid var(--border)",
              }}
            >
              <span
                style={{
                  flex: "0 0 auto",
                  width: 20,
                  height: 20,
                  borderRadius: 5,
                  background: MATCLR_SUP[k],
                  color: "#fff",
                  fontFamily: "var(--font-mono)",
                  fontWeight: 800,
                  fontSize: 11,
                  display: "grid",
                  placeItems: "center",
                }}
              >
                {k === "na" ? "NA" : k}
              </span>
              <span
                style={{
                  fontSize: "var(--text-2xs)",
                  lineHeight: 1.32,
                  color: on ? "var(--text)" : "var(--text-muted)",
                }}
              >
                <b style={{ color: on ? "var(--text)" : "var(--slate-600)" }}>{TITULO_SUP[k]}</b> —{" "}
                {RUBRICA_SUP[k]}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  foot,
  color,
}: {
  label: string;
  value: ReactNode;
  foot?: string;
  color?: string;
}) {
  return (
    <div className="sup-stat">
      <div className="l">{label}</div>
      <div className="v" style={color ? { color } : undefined}>
        {value}
      </div>
      {foot && <div className="f">{foot}</div>}
    </div>
  );
}

function SupAlert({ ico = "∑", titulo, children }: { ico?: string; titulo: string; children: ReactNode }) {
  return (
    <div className="sup-alert">
      <span className="sup-alert-ico">{ico}</span>
      <div>
        <div className="sup-alert-title">{titulo}</div>
        <div className="sup-alert-text">{children}</div>
      </div>
    </div>
  );
}

/* ---------- Camadas de processo (Pré-visita / Campo / Validação técnica) ---------- */

interface CriterioItem {
  id: string;
  criterio: string;
  crit: string;
  norma?: string;
  grupo?: string;
}

/** Agrupa critérios consecutivos por `grupo` (preserva a ordem do seed) para
 * exibir subcabeçalhos por etapa/seção. Listas sem `grupo` viram um bloco único. */
function agruparPorGrupo(lista: readonly CriterioItem[]): [string | undefined, CriterioItem[]][] {
  const out: [string | undefined, CriterioItem[]][] = [];
  for (const c of lista) {
    const ultimo = out[out.length - 1];
    if (ultimo && ultimo[0] === c.grupo) {
      ultimo[1].push(c);
    } else {
      out.push([c.grupo, [c]]);
    }
  }
  return out;
}

function CriterioLinha({
  c,
  dados,
  podeEditar,
  editar,
  complemento,
}: {
  c: CriterioItem;
  dados: SupervisaoData;
  podeEditar: boolean;
  editar: Editar;
  complemento?: ReactNode;
}) {
  const r = criterioOu(dados, c.id);
  const peso = pesoCriticidade(c.crit);
  const set = (patch: Partial<typeof r>) =>
    editar((d) => ({ ...d, criterios: { ...d.criterios, [c.id]: { ...criterioOu(d, c.id), ...patch } } }));
  // Fonte de verdade: `mat` (nova). Fallback legado: `resultado` → não exibido no MatSelect.
  const matAtual = r.mat ?? null;
  const resultadoEfetivo = matAtual != null ? matParaResultado(matAtual) : r.resultado;
  const isNC = resultadoEfetivo === "NC";
  return (
    <div className="sup-crit">
      <div className="sup-crit-head">
        <span className="chip chip-code">{c.id}</span>
        <CritBadge crit={c.crit} />
        <span className="spec" title={`Criticidade ${c.crit} → peso ${peso} no índice da fase.`}>
          peso {peso}
        </span>
        {resultadoEfetivo && (
          <span style={{ fontWeight: 700, fontSize: "var(--text-xs)", color: COR_RES[resultadoEfetivo] }}>
            {resultadoEfetivo}
          </span>
        )}
      </div>
      <div style={{ fontSize: "var(--text-sm)", lineHeight: 1.35 }}>{c.criterio}</div>
      {c.norma && <div className="spec">{c.norma}</div>}
      {complemento}
      {podeEditar ? (
        <MatSelect
          value={matAtual}
          onChange={(v) => set({ mat: v, resultado: matParaResultado(v), likert: null })}
        />
      ) : (
        <MatBadge m={mat(matAtual)} />
      )}
      <DSLegendaSup matAtual={matAtual} />
      {isNC && (
        <div className="spec" style={{ color: "var(--danger)" }}>
          ⛔ Não conformidade — registre a ação necessária.
        </div>
      )}
      <textarea
        className="m-textarea"
        placeholder="Registro / observação"
        value={r.registro ?? ""}
        disabled={!podeEditar}
        onChange={(e) => set({ registro: e.target.value })}
        style={{ minHeight: 48 }}
      />
    </div>
  );
}

/** Linha de checagem com símbolo de "atende / não atende / sem dado". */
function ChecagemLinha({ ok, children }: { ok: boolean | null; children: ReactNode }) {
  const ico = ok === null ? "•" : ok ? "✓" : "⚠";
  const cor = ok === null ? "var(--text-muted)" : ok ? "var(--success)" : "var(--warning)";
  return (
    <li style={{ display: "flex", gap: 6, alignItems: "baseline" }}>
      <span style={{ color: cor, fontWeight: 700 }}>{ico}</span>
      <span>{children}</span>
    </li>
  );
}

/**
 * Diagnóstico ORIENTATIVO do cálculo de amostragem (exibido na aba Amostra) —
 * guia o julgamento da cobertura. NÃO altera o C/NC/NA. Quando há amostra gerada,
 * mostra o comparativo real × plano e a cobertura de estratos; senão, mantém as
 * perguntas-guia textuais.
 */
function SugestoesVTP06({ dados, alvo }: { dados: SupervisaoData; alvo: number }) {
  const d = diagnosticoAmostragem(dados.amostra, alvo);
  return (
    <div
      className="m-card pad"
      style={{ margin: "6px 0", background: "var(--surface-2)", fontSize: "var(--text-sm)" }}
    >
      <div style={{ fontWeight: 600, marginBottom: 4 }}>🧭 Sugestões para verificar o cálculo</div>
      <p className="spec" style={{ marginTop: 0 }}>
        Orientações de apoio — <b>não alteram o C/NC/NA</b>; a decisão é sua.
      </p>
      {d.temDados ? (
        <ul style={{ margin: "4px 0 0", paddingLeft: 4, listStyle: "none", display: "grid", gap: 4 }}>
          <ChecagemLinha ok={d.atingiuPlano}>
            Tamanho da amostra: <b>{d.real}</b> itens vs. plano <b>{d.plano}</b>
            {d.atingiuPlano ? " (atinge o plano)" : " (abaixo do plano — verifique a justificativa)"}.
          </ChecagemLinha>
          <ChecagemLinha ok={d.estratosCobertos === 3}>
            Cobertura de estratos: CORE {d.porEstrato.CORE} · Risco inerente {d.porEstrato.RiscoInerente} ·
            Sorteio {d.porEstrato.Aleatorio}
            {d.estratosCobertos === 3 ? " (todos cobertos)" : " (algum estrato sem itens — confira)"}.
          </ChecagemLinha>
          <ChecagemLinha ok={null}>
            Houve <b>substituição</b> de itens da amostra? Se sim, está justificada?
          </ChecagemLinha>
          <ChecagemLinha ok={null}>
            A amostra dá <b>representatividade</b> suficiente para embasar o parecer?
          </ChecagemLinha>
        </ul>
      ) : (
        <ul style={{ margin: "4px 0 0", paddingLeft: 4, listStyle: "none", display: "grid", gap: 4 }}>
          <ChecagemLinha ok={null}>Amostra ainda não gerada (≥ {alvo} itens avaliados).</ChecagemLinha>
          <ChecagemLinha ok={null}>O tamanho observado bate com o calculado no plano (Aba 10)?</ChecagemLinha>
          <ChecagemLinha ok={null}>
            A seleção cobriu os estratos (CORE / risco inerente / sorteio)?
          </ChecagemLinha>
          <ChecagemLinha ok={null}>Houve substituição de itens? Foi justificada?</ChecagemLinha>
          <ChecagemLinha ok={null}>A amostra dá representatividade suficiente para o parecer?</ChecagemLinha>
        </ul>
      )}
    </div>
  );
}

const RES_EXIB: ResultadoSupervisao[] = ["C", "NC", "NA"];

function AbaCriterios({
  titulo,
  intro,
  ico,
  lista,
  dados,
  podeEditar,
  editar,
  complementos,
}: {
  titulo: string;
  intro: ReactNode;
  ico: string;
  lista: readonly CriterioItem[];
  dados: SupervisaoData;
  podeEditar: boolean;
  editar: Editar;
  complementos?: Record<string, ReactNode>;
}) {
  const conf = conformidadeFase(lista, dados);
  return (
    <div>
      <SupAlert ico={ico} titulo={titulo}>
        {intro}
      </SupAlert>
      <div className="m-card pad" style={{ marginBottom: 8 }}>
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "baseline" }}>
          <Stat
            label="Índice de aderência (ponderado)"
            value={conf.indice != null ? `${Math.round(conf.indice * 100)}%` : "—"}
            foot={`${conf.respondidos}/${conf.total} respondidos`}
            color={conf.indice != null ? COR_RES.C : undefined}
          />
          {RES_EXIB.map((rk) => (
            <div key={rk} className="spec">
              <b style={{ color: COR_RES[rk] }}>{rk}</b>: {conf.porResultado[rk].itens}{" "}
              {conf.porResultado[rk].itens === 1 ? "item" : "itens"}
              {rk !== "NA" && ` · peso ${conf.porResultado[rk].peso}`}
            </div>
          ))}
        </div>
        <div className="spec" style={{ marginTop: 4 }}>
          Índice = Σ(score ONA × peso) ÷ Σ(peso); criticidade Alta=
          {PESOS_CONFORMIDADE_CAMPO.criticidade.Alta}/Média=
          {PESOS_CONFORMIDADE_CAMPO.criticidade.Média}/Baixa=
          {PESOS_CONFORMIDADE_CAMPO.criticidade.Baixa}. NA excluído.
        </div>
      </div>
      <div className="m-card pad">
        {agruparPorGrupo(lista).map(([grupo, itens]) => (
          <div key={grupo ?? "_"}>
            {grupo && (
              <div
                style={{
                  fontWeight: 700,
                  fontSize: "var(--text-sm)",
                  color: "var(--teal-700)",
                  margin: "12px 0 6px",
                }}
              >
                {grupo}
              </div>
            )}
            {itens.map((c) => (
              <CriterioLinha
                key={c.id}
                c={c}
                dados={dados}
                podeEditar={podeEditar}
                editar={editar}
                complemento={complementos?.[c.id]}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Camada 2 — Amostra cega (tabela) ---------- */

function CorrFlag({ corr }: { corr: "Corrobora" | "Diverge" | null }) {
  if (!corr) {
    return <span className="spec">—</span>;
  }
  const ok = corr === "Corrobora";
  return (
    <span
      className="sup-flag"
      style={{
        background: ok ? "var(--mat-3-soft)" : "var(--danger-soft)",
        color: ok ? "var(--mat-4)" : "var(--danger)",
      }}
    >
      {corr}
    </span>
  );
}

function StatusFlag({ corr }: { corr: "Corrobora" | "Diverge" | null }) {
  if (corr === "Diverge") {
    return (
      <span className="sup-flag" style={{ color: "var(--danger)", border: "1px solid currentColor" }}>
        ↩ Revisar
      </span>
    );
  }
  if (corr === "Corrobora") {
    return (
      <span className="sup-flag" style={{ color: "var(--mat-4)", border: "1px solid currentColor" }}>
        ✓ Validar
      </span>
    );
  }
  return <span className="spec">—</span>;
}

function AmostraRow({
  a,
  nota,
  corr,
  evidencia,
  podeEditar,
  setMat,
}: {
  a: SupervisaoData["amostra"][number];
  nota: string | null;
  corr: "Corrobora" | "Diverge" | null;
  evidencia: string | null;
  podeEditar: boolean;
  setMat: (itemId: string, v: NivelMaturidade) => void;
}) {
  const cat = itemDoCatalogo(a.itemId);
  // Cegueira: a nota do avaliador só é revelada DEPOIS que o supervisor lança a
  // sua (matSup). Antes disso a re-performance é cega; o merge vem em seguida.
  const revelado = a.matSup != null;
  return (
    <tr>
      <td>
        <span className="chip chip-code">{a.itemId}</span>
      </td>
      <td style={{ maxWidth: 230 }}>
        {a.estrato}
        <div className="spec" style={{ marginTop: 2 }}>
          {cat?.pergunta}
        </div>
      </td>
      <td>
        {revelado ? (
          <MatBadge m={mat(nota)} />
        ) : (
          <span className="spec" title="Oculta até você lançar sua nota (re-performance cega)">
            🔒 oculta
          </span>
        )}
      </td>
      <td style={{ maxWidth: 220 }}>
        {revelado ? (
          <span className="spec" style={{ whiteSpace: "pre-wrap" }}>
            {evidencia?.trim() ? evidencia : <i>— sem registro</i>}
          </span>
        ) : (
          <span className="spec" title="Oculta até você lançar sua nota (re-performance cega)">
            🔒 oculta
          </span>
        )}
      </td>
      <td style={{ minWidth: 168 }}>
        {podeEditar ? (
          <MatSelect value={a.matSup ?? null} onChange={(v) => setMat(a.itemId, v)} />
        ) : (
          <MatBadge m={mat(a.matSup)} />
        )}
      </td>
      <td>
        <CorrFlag corr={corr} />
      </td>
      <td>
        <StatusFlag corr={corr} />
      </td>
    </tr>
  );
}

/** Card de amostra — renderizado em telas estreitas (≤640px) no lugar da tabela. */
function AmostraCard({
  a,
  nota,
  corr,
  evidencia,
  podeEditar,
  setMat,
}: {
  a: SupervisaoData["amostra"][number];
  nota: string | null;
  corr: "Corrobora" | "Diverge" | null;
  evidencia: string | null;
  podeEditar: boolean;
  setMat: (itemId: string, v: NivelMaturidade) => void;
}) {
  const cat = itemDoCatalogo(a.itemId);
  const revelado = a.matSup != null;
  return (
    <div className="sup-amostra-card">
      <div className="sup-amostra-card-head">
        <span className="chip chip-code">{a.itemId}</span>
        <span className="sup-flag" style={{ background: "var(--surface-2)", color: "var(--text-muted)" }}>
          {a.estrato}
        </span>
        <span style={{ flex: 1 }} />
        <CorrFlag corr={corr} />
      </div>
      {cat && (
        <div style={{ fontSize: "var(--text-sm)", lineHeight: 1.4, color: "var(--text-muted)" }}>
          {cat.pergunta}
        </div>
      )}
      <div className="sup-amostra-card-notas">
        <div>
          <div className="sup-amostra-card-nota-label">Nota do avaliador</div>
          {revelado ? (
            <MatBadge m={mat(nota)} />
          ) : (
            <span
              className="sup-flag"
              style={{ background: "var(--surface-2)", color: "var(--text-muted)" }}
              title="Oculta até você lançar sua nota"
            >
              🔒 oculta
            </span>
          )}
        </div>
        <div>
          <div className="sup-amostra-card-nota-label">Nota do supervisor</div>
          {podeEditar ? (
            <MatSelect value={a.matSup ?? null} onChange={(v) => setMat(a.itemId, v)} />
          ) : (
            <MatBadge m={mat(a.matSup)} />
          )}
        </div>
      </div>
      <div>
        <div className="sup-amostra-card-nota-label">Evidência do avaliador</div>
        {revelado ? (
          <div className="spec" style={{ whiteSpace: "pre-wrap" }}>
            {evidencia?.trim() ? evidencia : <i>— sem registro</i>}
          </div>
        ) : (
          <span
            className="sup-flag"
            style={{ background: "var(--surface-2)", color: "var(--text-muted)" }}
            title="Oculta até você lançar sua nota"
          >
            🔒 oculta
          </span>
        )}
      </div>
      <StatusFlag corr={corr} />
    </div>
  );
}

function AbaAmostra({
  dados,
  podeEditar,
  editar,
  notaPorItem,
  evidenciaPorItem,
  alvo,
  checklistCompleto,
  faltamCompletar,
  liberarAmostra,
  podeLiberarAmostra,
}: {
  dados: SupervisaoData;
  podeEditar: boolean;
  editar: Editar;
  notaPorItem: Map<string, string | null>;
  evidenciaPorItem: Map<string, string | null>;
  /** Alvo efetivo da amostra: `min(28, nAplicáveis)` (doc 17b §3.5). */
  alvo: number;
  /** Checklist do escopo concluído (libera o sorteio). */
  checklistCompleto: boolean;
  /** Itens do escopo ainda sem maturidade + evidência (NA dispensa evidência). */
  faltamCompletar: number;
  /** Libera a amostra + sorteio novo + reset das contestações (Supervisão). */
  liberarAmostra: () => void;
  /** Mostra o botão liberar/refazer (trava estrita não passa e pode editar). */
  podeLiberarAmostra: boolean;
}) {
  const linhas = dados.amostra.map((a) => {
    const nota = notaPorItem.get(a.itemId) ?? null;
    return { a, nota, evidencia: evidenciaPorItem.get(a.itemId) ?? null, corr: corroboracao(a.matSup, nota) };
  });
  const total = linhas.length;
  const corrob = linhas.filter((l) => l.corr === "Corrobora").length;
  const div = linhas.filter((l) => l.corr === "Diverge").length;
  const merged = linhas.filter((l) => l.corr != null).length;
  const taxa = merged ? corrob / merged : null;
  // Define matSup e já sincroniza o status (Diverge → Revisar, senão Validar).
  const setMat = (itemId: string, v: NivelMaturidade) =>
    editar((d) => ({
      ...d,
      amostra: d.amostra.map((x) =>
        x.itemId === itemId
          ? {
              ...x,
              matSup: v,
              status: corroboracao(v, notaPorItem.get(itemId) ?? null) === "Diverge" ? "Revisar" : "Validar",
            }
          : x,
      ),
    }));
  // Botão da Supervisão (só quando a trava estrita não passa): libera a amostra
  // OU, se já liberada, refaz o sorteio. Em ambos os casos reseta as contestações
  // (amostra e contestação são interligadas). Aparece nos dois estados abaixo.
  const botaoLiberar = podeLiberarAmostra ? (
    <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
      <button
        type="button"
        className="m-btn"
        onClick={() => {
          if (
            window.confirm(
              "Isto descarta a amostra cega atual e as contestações e sorteia uma NOVA amostra. Continuar?",
            )
          ) {
            liberarAmostra();
          }
        }}
      >
        {checklistCompleto ? "↻ Refazer amostra (novo sorteio)" : "🔓 Liberar amostra cega"}
      </button>
    </div>
  ) : null;

  // Gating: a amostra só é sorteada quando o checklist do escopo está COMPLETO
  // (todo item com maturidade + evidência) — garante aleatoriedade sobre um
  // checklist concluído. Apenas itens 2-4 entram no sorteio (doc 09 §9.5).
  if (!checklistCompleto) {
    return (
      <div>
        {botaoLiberar}
        <SupAlert titulo="Camada 2 · Re-performance cega por risco">
          A amostra é sorteada quando os avaliadores concluírem pelo menos <b>{PCT_LIBERA}%</b> do checklist
          do escopo (maturidade + evidência em cada item; NA dispensa evidência). Assim o sorteio é aleatório
          sobre um checklist quase completo, sem esperar o último item. Entram no sorteio só itens avaliados
          em <b>2, 3 ou 4</b>.
        </SupAlert>
        <div className="m-card pad" style={{ textAlign: "center" }}>
          <p style={{ fontWeight: 600, marginBottom: 4 }}>
            Amostra disponível com ≥ {PCT_LIBERA}% do checklist concluído
          </p>
          <p className="spec">
            {faltamCompletar > 0
              ? `Faltam ${faltamCompletar} ${faltamCompletar === 1 ? "item" : "itens"} para atingir o mínimo de ${PCT_LIBERA}% (maturidade + evidência).`
              : `Aguardando atingir ${PCT_LIBERA}% do checklist no escopo.`}
          </p>
        </div>
      </div>
    );
  }
  return (
    <div>
      {botaoLiberar}
      <SupAlert titulo="Camada 2 · Re-performance cega por risco">
        O Supervisor re-avalia uma amostra de itens de forma independente,{" "}
        <b>sem ver a nota nem a evidência do avaliador</b> (reveladas só após lançar a sua). Entram no sorteio
        só itens avaliados em <b>2-4</b>. <b>Corrobora</b> quando a nota difere em ≤ 1 nível; <b>Diverge</b>{" "}
        quando difere ≥ 2 (ou apenas um marcou NA). Itens que divergem voltam aos avaliadores (Revisar).
      </SupAlert>
      <div className="sup-stats">
        <Stat label="Itens amostrados" value={total} foot="estratificados por risco" />
        <Stat label="Corroboram" value={corrob} foot="≤ 1 nível" color="var(--success)" />
        <Stat
          label="Divergem"
          value={div}
          foot="voltam p/ revisão"
          color={div ? "var(--danger)" : undefined}
        />
        <Stat
          label="Corroboração"
          value={taxa == null ? "—" : `${Math.round(taxa * 100)}%`}
          foot="meta ≥ 80%"
          color={taxa != null && taxa >= 0.8 ? "var(--success)" : "var(--warning)"}
        />
      </div>
      <SugestoesVTP06 dados={dados} alvo={alvo} />
      {total === 0 ? (
        <p className="spec">Amostra ainda não gerada pelo Supervisor.</p>
      ) : (
        <>
          {/* Tabela — visível em telas largas (≥641px) */}
          <div className="sup-table-wrap">
            <table className="sup-table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Estrato / motivo</th>
                  <th>Nota avaliador</th>
                  <th>Evidência do avaliador</th>
                  <th>Nota do supervisor</th>
                  <th>Corroboração</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {linhas.map(({ a, nota, evidencia, corr }) => (
                  <AmostraRow
                    key={a.itemId}
                    a={a}
                    nota={nota}
                    corr={corr}
                    evidencia={evidencia}
                    podeEditar={podeEditar}
                    setMat={setMat}
                  />
                ))}
              </tbody>
            </table>
          </div>
          {/* Cards — visíveis em telas estreitas (≤640px) */}
          <div className="sup-amostra-cards">
            {linhas.map(({ a, nota, evidencia, corr }) => (
              <AmostraCard
                key={a.itemId}
                a={a}
                nota={nota}
                corr={corr}
                evidencia={evidencia}
                podeEditar={podeEditar}
                setMat={setMat}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ---------- Contestação — arbitragem das divergências (entra no IMGQ) ---------- */

function AbaContest({
  dados,
  podeEditar,
  editar,
  notaPorItem,
}: {
  dados: SupervisaoData;
  podeEditar: boolean;
  editar: Editar;
  notaPorItem: Map<string, string | null>;
}) {
  const divs = divergenciasAmostra(dados, notaPorItem);
  const resol = dados.resolucoes ?? {};
  const upsert = (itemId: string, patch: { escolha?: EscolhaResolucao; justificativa?: string }) =>
    editar((d) => ({
      ...d,
      resolucoes: {
        ...(d.resolucoes ?? {}),
        [itemId]: { escolha: "avaliador", ...(d.resolucoes?.[itemId] ?? {}), ...patch },
      },
    }));
  return (
    <div>
      <SupAlert ico="⚖" titulo="Contestação · arbitragem das divergências (entra no IMGQ)">
        Itens em que a maturidade cega do Supervisor <b>diverge</b> (≥ 2 níveis) da nota do avaliador. Escolha
        qual nota prevalece: <b>Adotar supervisor</b> substitui a maturidade do item no cálculo do IMGQ;{" "}
        <b>Manter avaliador</b> mantém a original.
      </SupAlert>
      {divs.length === 0 ? (
        <p className="spec">Nenhuma divergência entre avaliador e Supervisor na amostra.</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
          {divs.map((dv) => {
            const cat = itemDoCatalogo(dv.itemId);
            const escolha = resol[dv.itemId]?.escolha ?? "avaliador";
            return (
              <div key={dv.itemId} className="m-card pad">
                <div
                  style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}
                >
                  <span className="chip chip-code">{dv.itemId}</span>
                  <span
                    className="sup-flag"
                    style={{ background: "var(--danger-soft)", color: "var(--danger)" }}
                  >
                    Diverge
                  </span>
                  <span style={{ flex: 1 }} />
                  <div className="sup-restoggle">
                    <button
                      type="button"
                      className={escolha === "avaliador" ? "on" : ""}
                      disabled={!podeEditar}
                      style={escolha === "avaliador" ? { color: "var(--primary)" } : undefined}
                      onClick={() => upsert(dv.itemId, { escolha: "avaliador" })}
                    >
                      Manter avaliador
                    </button>
                    <button
                      type="button"
                      className={escolha === "supervisor" ? "on" : ""}
                      disabled={!podeEditar}
                      style={escolha === "supervisor" ? { color: "var(--primary)" } : undefined}
                      onClick={() => upsert(dv.itemId, { escolha: "supervisor" })}
                    >
                      Adotar supervisor
                    </button>
                  </div>
                </div>
                <div style={{ fontSize: "var(--text-sm)", fontWeight: 600, marginBottom: 8 }}>
                  {cat?.pergunta}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-3)" }}>
                  <div>
                    <div className="spec">Nota do avaliador</div>
                    <div style={{ marginTop: 4 }}>
                      <MatBadge m={dv.notaAval} />
                    </div>
                  </div>
                  <div>
                    <div className="spec">Nota do Supervisor (cega)</div>
                    <div style={{ marginTop: 4 }}>
                      <MatBadge m={dv.matSup} />
                    </div>
                  </div>
                </div>
                <textarea
                  className="m-textarea"
                  placeholder="Justificativa técnica da decisão (opcional)"
                  value={resol[dv.itemId]?.justificativa ?? ""}
                  disabled={!podeEditar}
                  onChange={(e) => upsert(dv.itemId, { justificativa: e.target.value })}
                  style={{ marginTop: 10, minHeight: 46 }}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ---------- Camada 3 — Parecer ---------- */

function AbaParecer({
  dados,
  podeEditar,
  editar,
  ind,
  salvar,
  salvando,
  online,
  msg,
}: {
  dados: SupervisaoData;
  podeEditar: boolean;
  editar: Editar;
  ind: IndicadoresParecer;
  salvar: (validar: boolean) => void;
  salvando: boolean;
  online: boolean;
  msg: string | null;
}) {
  const taxa = ind.taxaCorroboracao;
  // Itens que voltam aos avaliadores (divergência cega ≥ 2 níveis).
  const aRevisar = dados.amostra.filter((a) => a.status === "Revisar");
  // Critérios de validação de ALTA criticidade em Não Conforme (pesam no parecer).
  const validacaoNC = SUP_VALIDACAO.filter((c) => {
    if (c.crit !== "Alta") return false;
    const r = criterioOu(dados, c.id);
    const res = r.mat != null ? matParaResultado(r.mat) : r.resultado;
    return res === "NC";
  });
  return (
    <div>
      <SupAlert ico="✓" titulo="Camada 3 · Parecer de confiabilidade">
        O selo só é oficial após <b>Validar</b>. As decisões da contestação já entram no IMGQ.
      </SupAlert>
      <div className="sup-stats" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
        <Stat
          label="Validação NC (alto risco)"
          value={ind.metaNcAlta}
          color={ind.metaNcAlta ? "var(--danger)" : "var(--success)"}
        />
        <Stat label="Corroboração" value={taxa == null ? "—" : `${Math.round(taxa * 100)}%`} />
        <Stat label="A revisar" value={ind.itensRevisar} />
      </div>

      {/* Detalhamento dos itens que pesam no parecer (não só o KPI). */}
      {(aRevisar.length > 0 || validacaoNC.length > 0) && (
        <div style={{ display: "grid", gap: "var(--space-3)", marginBottom: 12 }}>
          {aRevisar.length > 0 && (
            <div className="m-card pad">
              <div className="panel-label" style={{ marginBottom: 8, color: "var(--danger)" }}>
                ↩ Itens a revisar — divergência cega ({aRevisar.length})
              </div>
              <div style={{ display: "grid", gap: 6 }}>
                {aRevisar.map((a) => {
                  const cat = itemDoCatalogo(a.itemId);
                  return (
                    <div key={a.itemId} style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
                      <span className="chip chip-code">{a.itemId}</span>
                      <span style={{ fontSize: "var(--text-sm)", lineHeight: 1.35 }}>{cat?.pergunta}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {validacaoNC.length > 0 && (
            <div className="m-card pad">
              <div className="panel-label" style={{ marginBottom: 8, color: "var(--danger)" }}>
                ⛔ Validação · Não Conforme de alto risco ({validacaoNC.length})
              </div>
              <div style={{ display: "grid", gap: 6 }}>
                {validacaoNC.map((c) => (
                  <div key={c.id} style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
                    <span className="chip chip-code">{c.id}</span>
                    <span style={{ fontSize: "var(--text-sm)", lineHeight: 1.35 }}>{c.criterio}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {ind.pareceSugerido && (
        <p className="spec" style={{ marginBottom: 8 }}>
          Sugerido pela regra: <b>{ind.pareceSugerido}</b>
        </p>
      )}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
        {PARECERES.map((p) => (
          <button
            key={p.v}
            type="button"
            className={`m-btn${dados.parecer === p.v ? " primary" : ""}`}
            disabled={!podeEditar}
            onClick={() => editar((d) => ({ ...d, parecer: p.v }))}
          >
            {p.label}
          </button>
        ))}
      </div>
      {podeEditar && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            type="button"
            className="m-btn"
            disabled={salvando || !online}
            onClick={() => salvar(false)}
          >
            {salvando ? "Salvando…" : "Salvar supervisão"}
          </button>
          <button
            type="button"
            className="m-btn primary"
            disabled={salvando || !online || !dados.parecer}
            onClick={() => salvar(true)}
          >
            Validar avaliação
          </button>
        </div>
      )}
      {!online && (
        <p className="spec" style={{ marginTop: 8 }}>
          Offline — o preenchimento fica guardado no aparelho e sobe sozinho quando houver conexão. A{" "}
          <b>validação</b> precisa de internet.
        </p>
      )}
      {msg && (
        <p className="spec" style={{ marginTop: 8, color: "var(--primary)" }}>
          {msg}
        </p>
      )}
    </div>
  );
}

export interface SupervisaoChecklistProps {
  dados: SupervisaoData;
  podeEditar: boolean;
  editar: Editar;
  enviar: (validar?: boolean) => Promise<string | null>;
  itens: readonly ItemMerged[];
  /** Alvo efetivo da amostra: `min(28, nAplicáveis)` (doc 17b §3.5). */
  amostraAlvo: number;
  /** Checklist do escopo concluído (maturidade + evidência em todo item; libera a amostra). */
  checklistCompleto: boolean;
  /** Libera a amostra + sorteio novo + reset das contestações (repetível). */
  liberarAmostra: () => void;
  /** Mostra o botão liberar/refazer (trava estrita não passa e pode editar). */
  podeLiberarAmostra: boolean;
  online: boolean;
  /** Abre a versão imprimível (em branco) do checklist do supervisor. */
  onImprimir?: () => void;
}

// Ordem de exibição das abas (só apresentação; os códigos internos não mudam).
// Validação vem ANTES da Amostra cega. O Parecer (Camada 3) lê do blob, não da
// aba ativa, então a ordem visual não afeta a consolidação.
const ABAS: [string, string][] = [
  ["pre", "1 · Pré-visita"],
  ["campo", "2 · Campo"],
  ["vt", "3 · Validação"],
  ["amostra", "4 · Amostra cega"],
  ["contest", "5 · Contestações"],
  ["parecer", "6 · Parecer"],
];

export function SupervisaoChecklist({
  dados,
  podeEditar,
  editar,
  enviar,
  itens,
  amostraAlvo,
  checklistCompleto,
  liberarAmostra,
  podeLiberarAmostra,
  online,
  onImprimir,
}: SupervisaoChecklistProps) {
  const [aba, setAba] = useState<string>("pre");
  const [msg, setMsg] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const ind = useMemo(() => indicadoresParecer(dados, itens), [dados, itens]);
  const notaPorItem = useMemo(() => new Map(itens.map((it) => [it.id, it.mi ?? null])), [itens]);
  const evidenciaPorItem = useMemo(
    () => new Map(itens.map((it) => [it.id, it.evidenciaMi ?? null])),
    [itens],
  );
  // Quantos itens ainda faltam para atingir o MÍNIMO de liberação (≥ 90% do
  // escopo completo). NA dispensa evidência; o que importa é completar a régua.
  const faltamCompletar = useMemo(() => {
    const completos = itens.filter((it) => itemCompleto(it.mi, it.evidenciaMi)).length;
    return Math.max(0, minimoParaLiberar(itens.length) - completos);
  }, [itens]);

  const salvar = async (validar: boolean) => {
    setSalvando(true);
    setMsg(null);
    const erro = await enviar(validar);
    setMsg(erro ?? (validar ? "Avaliação validada (selo oficial)." : "Supervisão salva."));
    setSalvando(false);
  };

  const conteudo: Record<string, ReactNode> = {
    pre: (
      <AbaCriterios
        titulo="Camada de processo · Pré-visita"
        ico="①"
        intro="Verifique a preparação antes da partida (documentos, planejamento, logística)."
        lista={SUP_PRE}
        dados={dados}
        podeEditar={podeEditar}
        editar={editar}
      />
    ),
    campo: (
      <AbaCriterios
        titulo="Camada de processo · Supervisão em campo"
        ico="②"
        intro="Avalie a conduta dos avaliadores durante a visita (método, evidências, imparcialidade) usando a escala de aderência 0–4/NA."
        lista={SUP_CAMPO}
        dados={dados}
        podeEditar={podeEditar}
        editar={editar}
      />
    ),
    vt: (
      <AbaCriterios
        titulo="Validação independente da auditoria"
        ico="③"
        intro="Validação em 3 etapas (documental, in loco e critérios), sem reavaliar a nota. Use a escala 0–4/NA. Critérios de alta criticidade com mat ≤ 2 pesam no parecer."
        lista={SUP_VALIDACAO}
        dados={dados}
        podeEditar={podeEditar}
        editar={editar}
      />
    ),
    amostra: (
      <AbaAmostra
        dados={dados}
        podeEditar={podeEditar}
        editar={editar}
        notaPorItem={notaPorItem}
        evidenciaPorItem={evidenciaPorItem}
        alvo={amostraAlvo}
        checklistCompleto={checklistCompleto}
        faltamCompletar={faltamCompletar}
        liberarAmostra={liberarAmostra}
        podeLiberarAmostra={podeLiberarAmostra}
      />
    ),
    contest: <AbaContest dados={dados} podeEditar={podeEditar} editar={editar} notaPorItem={notaPorItem} />,
    parecer: (
      <AbaParecer
        dados={dados}
        podeEditar={podeEditar}
        editar={editar}
        ind={ind}
        salvar={(v) => void salvar(v)}
        salvando={salvando}
        online={online}
        msg={msg}
      />
    ),
  };

  return (
    <Fragment>
      {!podeEditar && (
        <p className="spec" style={{ marginBottom: 8 }}>
          👁️ Somente leitura — editam a Supervisão (AUDIN) e a gestão (admin, coordenação, presidência).
        </p>
      )}
      <div className="sup-tab-bar">
        {ABAS.map(([k, lbl]) => {
          // A aba fica navegável quando o botão liberar/refazer está disponível
          // (senão não haveria como alcançá-lo com o checklist ainda travado).
          const bloqueada = k === "amostra" && !checklistCompleto && !podeLiberarAmostra;
          return (
            <button
              key={k}
              type="button"
              className={aba === k ? "on" : ""}
              disabled={bloqueada}
              title={
                bloqueada ? `Disponível com ≥ ${PCT_LIBERA}% do checklist do escopo concluído` : undefined
              }
              onClick={() => setAba(k)}
            >
              {lbl}
            </button>
          );
        })}
        {onImprimir && (
          <button type="button" className="sup-tab-bar-print" onClick={onImprimir}>
            🖨 Imprimir
          </button>
        )}
      </div>
      {conteudo[aba]}
    </Fragment>
  );
}
